import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Briefcase, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    linkedin_url: "",
    cover_letter: ""
  });

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`${API}/jobs/${jobId}`);
        setJob(response.data);
      } catch (err) {
        setError("Job posting not found");
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/jobs/${jobId}/apply`, {
        job_id: jobId,
        ...formData
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Job Posting Not Found</h1>
          <Link to="/careers">
            <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Careers
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="job-detail-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link 
            to="/careers" 
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Careers
          </Link>
          
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-blue-700/30 text-blue-300 text-xs font-medium border border-blue-700">
                {job.department}
              </span>
              <span className="px-3 py-1 bg-slate-700 text-slate-300 text-xs font-medium">
                {job.type}
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white font-['Outfit'] mb-6">
              {job.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-slate-300">
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-400" />
                {job.location}
              </span>
              <span className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-400" />
                {job.experience}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Posted {job.posted_at}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Job Details */}
            <div className="lg:col-span-2 space-y-10">
              {/* Description */}
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                  About This Role
                </h2>
                <p className="text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                  {job.description}
                </p>
              </div>

              {/* Responsibilities */}
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                  Responsibilities
                </h2>
                <ul className="space-y-3">
                  {job.responsibilities.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 font-['Plus_Jakarta_Sans']">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                  Requirements
                </h2>
                <ul className="space-y-3">
                  {job.requirements.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-600 font-['Plus_Jakarta_Sans']">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Benefits */}
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                  Benefits
                </h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {job.benefits.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 font-['Plus_Jakarta_Sans']">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Application Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-slate-50 p-8 border border-slate-200">
                <h2 className="text-xl font-semibold text-slate-900 font-['Outfit'] mb-6">
                  Apply for This Position
                </h2>
                
                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      Application Submitted!
                    </h3>
                    <p className="text-slate-600 text-sm">
                      Thank you for your interest. We'll review your application and get back to you soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1 rounded-none border-slate-300"
                        data-testid="job-apply-name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="text-slate-700">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1 rounded-none border-slate-300"
                        data-testid="job-apply-email"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="text-slate-700">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1 rounded-none border-slate-300"
                        data-testid="job-apply-phone"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="linkedin_url" className="text-slate-700">LinkedIn URL</Label>
                      <Input
                        id="linkedin_url"
                        name="linkedin_url"
                        type="url"
                        value={formData.linkedin_url}
                        onChange={handleChange}
                        placeholder="https://linkedin.com/in/yourprofile"
                        className="mt-1 rounded-none border-slate-300"
                        data-testid="job-apply-linkedin"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="cover_letter" className="text-slate-700">Cover Letter</Label>
                      <Textarea
                        id="cover_letter"
                        name="cover_letter"
                        value={formData.cover_letter}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Tell us why you're interested in this role..."
                        className="mt-1 rounded-none border-slate-300"
                        data-testid="job-apply-cover"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none"
                      data-testid="job-apply-submit"
                    >
                      {submitting ? "Submitting..." : "Submit Application"}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
